import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import api from "../lib/api";
import { statusLabel, statusClass, detailTr } from "../lib/format";
import { VariantBadge } from "./VariantBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";

const MONTHS_TR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const DOW_TR = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const cellIntensity = (u) =>
  u >= 6 ? "bg-[#1C4E35] text-white" : u >= 3 ? "bg-[#1C4E35]/70 text-white" : "bg-[#1C4E35]/20 text-[#1C4E35]";

export const ServiceCalendar = ({ spaceType, status, currency, organisation, zone }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthIdx, setMonthIdx] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/orders/service-calendar", {
      params: {
        space_type: spaceType || undefined,
        status: status !== "__all__" ? status : undefined,
        currency: currency !== "__all__" ? currency : undefined,
        organisation: organisation !== "__all__" ? organisation : undefined,
        zone: zone || undefined,
      },
    }).then((r) => {
      setDays(r.data.days);
      setSelectedDate(null);
      setMonthIdx(0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [spaceType, status, currency, organisation, zone]);

  const dayMap = useMemo(() => Object.fromEntries(days.map((d) => [d.date, d])), [days]);
  const months = useMemo(() => {
    const set = [...new Set(days.map((d) => d.date.slice(0, 7)))].sort();
    return set.length ? set : [];
  }, [days]);

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center" data-testid="calendar-loading">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#1C4E35] border-t-transparent" />
      </div>
    );
  }
  if (!months.length) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 text-stone-400" data-testid="calendar-empty">
        <CalendarDays size={26} strokeWidth={1.5} />
        <p className="text-sm">Bu filtrelerde gün seçimli servis siparişi bulunamadı</p>
      </div>
    );
  }

  const ym = months[Math.min(monthIdx, months.length - 1)];
  const [year, month] = ym.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const monthTotal = days.filter((d) => d.date.startsWith(ym)).reduce((a, d) => a + d.units, 0);
  const selected = selectedDate ? dayMap[selectedDate] : null;

  return (
    <div className="space-y-4" data-testid="service-calendar">
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setMonthIdx((i) => Math.max(0, i - 1))} disabled={monthIdx === 0} data-testid="calendar-prev-month" className="border border-stone-300 p-1.5 text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-30">
            <ChevronLeft size={14} />
          </button>
          <p className="font-heading min-w-[130px] text-center text-sm font-black tracking-tight" data-testid="calendar-month-label">
            {MONTHS_TR[month - 1]} {year}
          </p>
          <button onClick={() => setMonthIdx((i) => Math.min(months.length - 1, i + 1))} disabled={monthIdx >= months.length - 1} data-testid="calendar-next-month" className="border border-stone-300 p-1.5 text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-30">
            <ChevronRight size={14} />
          </button>
        </div>
        <p className="text-xs text-stone-500" data-testid="calendar-month-total">Bu ay toplam <strong className="text-[#1C4E35]">{monthTotal}</strong> hizmet-gün</p>
      </div>
      <div className="grid grid-cols-7 gap-px border-t border-stone-200 bg-stone-200">
        {DOW_TR.map((d) => (
          <div key={d} className="bg-stone-50 py-1.5 text-center text-[10px] uppercase tracking-wider text-stone-500">{d}</div>
        ))}
        {Array.from({ length: firstDow }).map((_, i) => (
          <div key={`b${i}`} className="min-h-[68px] bg-white" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const dateStr = `${ym}-${String(i + 1).padStart(2, "0")}`;
          const info = dayMap[dateStr];
          const active = selectedDate === dateStr;
          return (
            <button
              key={dateStr}
              onClick={() => info && setSelectedDate(active ? null : dateStr)}
              data-testid={`calendar-day-${dateStr}`}
              className={`min-h-[68px] bg-white p-1.5 text-left align-top transition-colors ${info ? "cursor-pointer hover:bg-stone-50" : "cursor-default"} ${active ? "ring-2 ring-inset ring-[#1C4E35]" : ""}`}
            >
              <span className="text-[11px] text-stone-400">{i + 1}</span>
              {info && (
                <span className={`mt-1 block px-1.5 py-1 text-center text-[11px] font-bold ${cellIntensity(info.units)}`}>
                  {info.units} hizmet
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="fade-up border-t border-stone-200 px-4 pb-4" data-testid="calendar-day-detail">
          <p className="font-heading py-3 text-sm font-black tracking-tight">
            {parseInt(selected.date.slice(8), 10)} {MONTHS_TR[month - 1]} {year} · {selected.units} hizmet
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Hizmet</TableHead>
                <TableHead className="text-xs">Seçim</TableHead>
                <TableHead className="text-xs">Kurum</TableHead>
                <TableHead className="text-xs">Durum</TableHead>
                <TableHead className="text-right text-xs">Adet</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selected.items.map((x, i) => (
                <TableRow key={`${x.ref}-${i}`} data-testid={`calendar-item-${i}`}>
                  <TableCell className="py-2">
                    <p className="text-sm font-medium">{x.name}</p>
                    <p className="text-[11px] text-stone-400">{x.sku} · {detailTr(x.detail)}</p>
                  </TableCell>
                  <TableCell className="py-2"><VariantBadge variant={x.variant} /></TableCell>
                  <TableCell className="py-2 max-w-[220px] truncate text-xs" title={x.organisation}>{x.organisation}</TableCell>
                  <TableCell className="py-2">
                    <span className={`border px-2 py-0.5 text-[10px] font-medium ${statusClass(x.status)}`}>{statusLabel(x.status)}</span>
                  </TableCell>
                  <TableCell className="py-2 text-right text-sm font-medium">{x.unitsPerDay}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
